<template>
  <v-container grid-list-md>
    <v-layout row wrap>
      <template v-if="!invoice">
        <v-flex xs12 text-xs-center>
          <v-progress-circular
            size="75"
            width="5"
            color="accent"
            class="ma-5"
            indeterminate
          />
        </v-flex>
      </template>
      <template v-else>
        <v-flex xs12>
          <v-card>
            <v-card-title primary-title>
              <div>
                <h1 class="display-1">Invoice #{{ invoice.id }}</h1>
                <span class="caption">{{ invoice.created_at.toLocaleDateString() }} at {{ invoice.created_at.toLocaleTimeString() }}</span>
              </div>
            </v-card-title>
            <v-card-text>
              <v-layout row wrap>
                <v-flex xs12>
                  <p><v-icon>date_range</v-icon> Billing period: {{ invoice.periodStart.toLocaleDateString() }} - {{ invoice.periodEnd.toLocaleDateString() }}</p>
                  <p>
                    <v-icon :color="statusIconColor">{{ statusIcon }}</v-icon>
                    {{ statusText }}
                  </p>
                </v-flex>
              </v-layout>
            </v-card-text>
            <v-card-actions>
              <v-btn flat :to="'/subscriptions/' + invoice.subscriptionID">
                View Subscription
              </v-btn>
              <v-btn
                v-if="invoice.datePaid === null"
                :loading="createPaymentLoading"
                :disabled="createPaymentLoading"
                flat
                @click="createPaypalPayment">
                Make Payment
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-flex>
        <v-flex xs12>
          <v-card>
            <v-card-title primary-title>
              <h1 class="display-1">Line Items</h1>
            </v-card-title>
            <v-card-text>
              <v-data-table
                :headers="linesHeaders"
                :pagination.sync="linesPagination"
                :items="lines"
                :loading="linesLoading"
                :hide-actions="true"
                item-key="id"
              >
                <template slot="items" slot-scope="props">
                  <td>{{ props.item.description }}</td>
                  <td>${{ props.item.price.toFixed(2) }}</td>
                  <td>{{ props.item.qty }}</td>
                  <td>${{ props.item.total.toFixed(2) }}</td>
                </template>
                <template v-slot:footer>
                  <td :colspan="linesHeaders.length" class="text-xs-right">
                    <v-layout column>
                      <v-flex>
                        <v-layout row wrap>
                          <v-flex grow>
                            <span>Total:</span>
                          </v-flex>
                          <v-flex xs1>
                            <span>&nbsp;${{ invoice.total.toFixed(2) }}&nbsp;</span>
                          </v-flex>
                        </v-layout>
                      </v-flex>
                      <v-flex class="primary--text">
                        <v-layout row wrap>
                          <v-flex grow>
                            <span>Payments:</span>
                          </v-flex>
                          <v-flex xs1>
                            <span>(${{ invoice.paymentsTotal.toFixed(2) }})</span>
                          </v-flex>
                        </v-layout>
                      </v-flex>
                      <v-flex class="font-weight-bold">
                        <v-layout row wrap>
                          <v-flex grow>
                            <span>Balance:</span>
                          </v-flex>
                          <v-flex xs1>
                            <span>&nbsp;${{ (invoice.total - invoice.paymentsTotal).toFixed(2) }}&nbsp;</span>
                          </v-flex>
                        </v-layout>
                      </v-flex>
                    </v-layout>
                  </td>
                </template>
              </v-data-table>
            </v-card-text>
          </v-card>
        </v-flex>
        <v-flex xs12>
          <v-card>
            <v-card-title primary-title>
              <h1 class="display-1">Payments</h1>
            </v-card-title>
            <v-card-text>
              <v-data-table
                :headers="paymentsHeaders"
                :pagination.sync="paymentsPagination"
                :items="payments"
                :loading="paymentsLoading"
                :hide-actions="true"
                item-key="id"
              >
                <template slot="items" slot-scope="props">
                  <td>{{ props.item.paymentDate.toLocaleDateString() }} at {{ props.item.paymentDate.toLocaleTimeString() }}</td>
                  <td>{{ props.item.userEmail }}</td>
                  <td>${{ props.item.paymentAmount.toFixed(2) }}</td>
                </template>
              </v-data-table>
            </v-card-text>
          </v-card>
        </v-flex>
      </template>
    </v-layout>
  </v-container>
</template>

<script>
export default {
  data: () => ({
    invoice: null,

    lines: [],
    linesLoading: false,
    linesHeaders: [
      { text: 'Item Description', value: 'description', sortable: false },
      { text: 'Price', value: 'price', sortable: false },
      { text: 'Qty', value: 'qty', sortable: false },
      { text: 'Total', value: 'total', sortable: false }
    ],
    linesPagination: {
      sortBy: 'index',
      rowsPerPage: -1
    },

    payments: [],
    paymentsLoading: false,
    createPaymentLoading: false,
    paymentsHeaders: [
      { text: 'Date', value: 'paymentDate', sortable: false },
      { text: 'User', value: 'userID', sortable: false },
      { text: 'Amount', value: 'paymentAmount', sortable: false }
    ],
    paymentsPagination: {
      sortBy: 'index',
      rowsPerPage: -1
    },

    users: []
  }),
  computed: {
    invoiceID: function () {
      return parseInt(this.$route.params.id)
    },
    statusIcon: function () {
      switch (this.statusIconColor) {
        case 'green':
          return 'check_circle_outline'
        case 'yellow':
          return 'warning'
        case 'red':
          return 'error_outline'
        default:
          return ''
      }
    },
    statusIconColor: function () {
      // Can't determine status until we have an invoice
      if (!this.invoice) {
        return ''
      }

      // If the invoice is paid, we're done
      if (this.invoice.datePaid !== null) {
        return 'green'
      } else {
        // If the due date hasn't passed, we warn; otherwise, we indicate error
        if (this.invoice.dateDue > new Date()) {
          return 'yellow'
        } else {
          return 'red'
        }
      }
    },
    statusText: function () {
      // Can't determine status until we have an invoice
      if (!this.invoice) {
        return ''
      }

      if (this.invoice.datePaid === null) {
        return 'Payment required by ' + this.invoice.dateDue.toLocaleDateString() + ' at ' + this.invoice.dateDue.toLocaleTimeString()
      } else {
        return 'Paid in full on ' + this.invoice.datePaid.toLocaleDateString()
      }
    }
  },
  methods: {
    createPaypalPayment: function () {
      this.createPaymentLoading = true
      this.axios.post('/invoices/payments/paypal', {
        invoiceID: this.invoiceID
      })
        .then((res) => {
          window.location.href = res.headers.location
        })
        .catch(this.$root.apiErrorHandler(() => {
          this.createPaymentLoading = false
          return true // standard error handling
        }))
    }
  },
  mounted: function () {
    this.axios.get('/invoices/' + this.invoiceID)
      .then((res) => {
        // If we didn't get a result, then something was wrong with the invoice
        // ID that was specified
        if (!res || !res.data || res.data.length === 0) {
          // XXX: Add an error message?
          return this.$router.push('/')
        }

        // Parse our date values into something more usable
        let invoice = res.data[0]
        let fields = [
          'created_at',
          'dateDue',
          'datePaid',
          'periodStart',
          'periodEnd'
        ]
        for (let field of fields) {
          if (invoice[field]) {
            invoice[field] = this.$root.parseApiDateTimeString(invoice[field])
          }
        }

        // Set our global invoice with a initial total values
        res.data[0].total = 0
        res.data[0].paymentsTotal = 0
        this.invoice = res.data[0]

        // Start loading line-item data
        this.linesLoading = true
        this.axios.get('/invoices/' + this.invoiceID + '/lines')
          .then((res) => {
            // Get a total for each line
            res.data.forEach((line) => {
              line.total = line.price * line.qty
            })

            // Get a total of all our line items
            this.invoice.total = res.data.reduce(function (t, v) {
              return t + (v.total)
            }, 0)

            // Set our global line items
            this.lines = res.data
          })
          .catch(this.$root.apiErrorHandler())
          .finally(() => {
            this.linesLoading = false
          })
        // Start loading payment data
        this.paymentsLoading = true
        this.axios.get('/invoices/payments/for/' + this.invoiceID)
          .then(async (res) => {
            // Get a total of all our payments
            this.invoice.paymentsTotal = res.data.reduce(function (t, v) {
              return t + (v.paymentAmount)
            }, 0)

            // Parse our date values into something more usable
            let fields = [
              'paymentDate'
            ]
            for (let payment of res.data) {
              for (let field of fields) {
                if (payment[field]) {
                  payment[field] = this.$root.parseApiDateTimeString(payment[field])
                }
              }
            }

            // Load in information about the user who made the payment
            for (let i in res.data) {
              let payment = res.data[i]

              // If we don't already have information about this payment's user,
              // load it now
              if (typeof this.users[payment.userID] === 'undefined') {
                this.users[payment.userID] = await (
                  this.axios.get('/auth/users/' + payment.userID)
                    .then((res) => {
                      return res.data[0]
                    })
                    .catch(() => {
                      return null
                    })
                )
              }

              // Add a simple email address value to our payment based on its user
              if (this.users[payment.userID] === null) {
                payment.userEmail = 'Admin'
              } else {
                let user = this.users[payment.userID]
                if (user.local.email) {
                  payment.userEmail = user.local.email
                } else if (user.google.email) {
                  payment.userEmail = user.google.email
                } else if (user.facebook.email) {
                  payment.userEmail = user.facebook.email
                } else {
                  payment.userEmail = 'Unknown'
                }
              }
            }

            // Set our global payments
            this.payments = res.data

            this.paymentsLoading = false
          })
          .catch(this.$root.apiErrorHandler(() => {
            this.paymentsLoading = false
            return true // standard error handling
          }))
      })
      .catch(this.$root.apiErrorHandler())
  }
}
</script>
